import React, { useEffect, useState } from "react";
import "./Login.css";
import vectorImage from "../../assets/images/upl-genie-logo.svg";
import { useLocation, useNavigate } from "../../hooks/useRouter";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PoweredBy from "../../assets/images/PoweredBy.svg";
import { encrypted, tokenManager } from "../../utils/utility.jsx";
import { handleAuthError } from "../../utils/apiHelper.js";

import { useDispatch } from "react-redux";
import { useRbacInit } from "../../hooks/useRbacInit";
import { useRbac } from "../../hooks/useRbac";
import { fetchAuthInfo, authenticateUser } from "../../store/apiActions.js";
import { useConstants } from "../../constant/constantProvider";
import { STORAGE_KEYS, QUERY_PARAMS, ROUTES, CONFIG } from "../../constant/keyConstants";
import useIsMobile from "../../hooks/useIsMobile";

function Login() {
  const dispatch = useDispatch();
  const { fetchRbac } = useRbacInit({ autoFetch: false }); // Don't auto-fetch, we'll call it after login
  const { getRouteFromRbacData } = useRbac();
  const {
    WELCOME_TO,
    LOGIN_TO_CONTINUE,
    LOGIN_BUTTON,
    PLEASE_WAIT_A_WHILE,
    POWERED_BY,
    LOGOUT_SUCCESS_MESSAGE
  } = useConstants();
  const [loginState, setLoginState] = useState(true);
  const [isLanding, setIsLanding] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    checkAndLoginUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Helper function to get the intended route from sessionStorage
  const getIntendedRoute = () => {
    const intendedRoute = sessionStorage.getItem(STORAGE_KEYS.INTENDED_ROUTE);
    if (intendedRoute) {
      sessionStorage.removeItem(STORAGE_KEYS.INTENDED_ROUTE);
      return intendedRoute;
    }
    return ROUTES.CHAT; // Default route
  };

  useEffect(() => {
    const token = tokenManager.getToken(STORAGE_KEYS.TOKEN);
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get(QUERY_PARAMS.CODE);

    if (token) {
      navigate(getIntendedRoute());
      return;
    }
    // If there is no token and no SSO code, show the landing page
    // so the user must explicitly click Login — do NOT auto-redirect.
    if (!code) {
      setIsLanding(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAndLoginUser = () => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get(QUERY_PARAMS.CODE);
    if (code) {
      setLoginState(false);
      fetchUserTokenAndAuthenticate(code);
    }
  };

  const fetchUserTokenAndAuthenticate = async (code) => {
    try {
      const body = { code: code };
      const response = await dispatch(authenticateUser(body));
      const item = response;
      tokenManager.setToken(item.token, STORAGE_KEYS.TOKEN);
      const rbacData = await fetchRbac(); // Await RBAC so we can resolve the correct landing route
      const encryptedLoginDetails = encrypted.encrypt(JSON.stringify(item.app_user));
      localStorage.setItem(STORAGE_KEYS.LOGGED_IN_DETAILS, encryptedLoginDetails);
      localStorage.setItem(STORAGE_KEYS.IS_POLICY, JSON.stringify(item.flag));
      localStorage.setItem(STORAGE_KEYS.IS_LOGIN, "true");
      localStorage.setItem(STORAGE_KEYS.SHOW_NEW_MODEL_INFO, "true");
      // localStorage.setItem(STORAGE_KEYS.SHOW_TOUR_GUIDE, JSON.stringify(item.flag));
      const userRole = item?.app_user?.role || null;
      // Save userRole to localStorage for backward compatibility
      if (userRole) {
        const encryptedUserRole = encrypted.encrypt(userRole);
        localStorage.setItem(STORAGE_KEYS.USER_ROLE, encryptedUserRole);
      }
      // Navigate: if user had a deep-linked page saved, honour it; otherwise use RBAC role-based routing
      const savedRoute = sessionStorage.getItem(STORAGE_KEYS.INTENDED_ROUTE);
      if (savedRoute) sessionStorage.removeItem(STORAGE_KEYS.INTENDED_ROUTE);
      navigate(savedRoute || getRouteFromRbacData(rbacData));
      setLoginState(true);
      setIsLanding(false);
      localStorage.setItem(STORAGE_KEYS.LOGOUT, "false");
    } catch (error) {
      setIsLanding(true);
      setLoginState(true);
      handleAuthError(error, toast);
    } finally {
      setLoginState(false); // Ensure the loading state is reset
    }
  };

  const fetchAuthenticatedAPI = () => {
    dispatch(fetchAuthInfo())
      .then((response) => {
        ssoSignIn(response);
      })
      .catch((error) => {
        setIsLanding(true);
        setLoginState(true);
        console.error("There was an error!", error);
        handleAuthError(error, toast);
      });
  };

  const ssoSignIn = (props) => {
    const item = props;
    const loginUrl = item.login_url;
    if (loginUrl) {
      window.location.href = loginUrl;
    }
  };

  useEffect(() => {
    const isLogout = localStorage.getItem(STORAGE_KEYS.LOGOUT) === "true";
    if (isLogout) {
      toast.info(LOGOUT_SUCCESS_MESSAGE);
      setIsLanding(true);
      localStorage.removeItem(STORAGE_KEYS.LOGOUT);
    }
  }, [LOGOUT_SUCCESS_MESSAGE]);


  return (
    <>
      <ToastContainer
        closeButton={true}
        autoClose={CONFIG.TOAST_AUTO_CLOSE}
        position={CONFIG.TOAST_POSITION}
      />
      {isLanding ? (
        // Landing Screen with Login Button
        <div
          className={`parentLoginContainer ${isMobile ? "mobile" : "desktop"}`}
        >
          <div className="parentTextContainer">
            <div className="textContainer">
              <div className="welcomeContainer">
                {WELCOME_TO}
              </div>
              <div>
                <img
                  className="login-logo-image"
                  src={vectorImage}
                  alt="vector"
                />
              </div>
              <div className="loginTo">
                <span>{LOGIN_TO_CONTINUE}</span>
              </div>
              <div className="loginButtonContainer">
                <button
                  className="button-style primary-button buttonAlignment"
                  onClick={fetchAuthenticatedAPI}
                >
                  {LOGIN_BUTTON}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : !loginState ? (
        // Please Wait Screen
        <div className="loginWaitContainer">
          <div className="sidebar-profile-sub-title">
            {PLEASE_WAIT_A_WHILE}
          </div>
        </div>
      ) : (
        // Welcome Screen
        <div className="loginWaitContainer">
          <div className="dashboard-profile-container">
            <span>
              <div className="sidebar-profile-sub-title">{WELCOME_TO}</div>
              <div>
                <img
                  className="login-welcome-image"
                  src={vectorImage}
                  alt="upl genie logo"
                />
              </div>
              <div className="loader-wrapper">
                <div className="loader-bar">
                  <div className="loader-fill"></div>
                </div>
              </div>
            </span>
            <div className="powered">
              <div className="powered-title">{POWERED_BY}</div>
              <div>
                <img src={PoweredBy} alt="" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Login;
